import React, { useMemo, useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';
import { formatCurrencyINR } from '../../../utils/currency';
import { useUserData } from '../../../hooks/useUserData';
import { buildApiUrl } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useDemo } from '../../../contexts/DemoContext';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { getChartTruncationConfig } from '../../../utils/chartUtils';

const DebtPaydownChart = ({ loans }) => {
  const { userData } = useUserData();

  const { getAuthHeaders } = useAuth();
  const { getDemoQueryParams, isDemoModeActive } = useDemo();
  const { canProjectBeyond5Years } = useSubscription();
  const [chartData, setChartData] = useState([]);
  const [top3Loans, setTop3Loans] = useState([]);
  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const queryParams = getDemoQueryParams();
        const res = await fetch(buildApiUrl(`/api/debt/timeline${queryParams}`), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load debt timeline');
        const json = await res.json();
        if (json?.success && json.data) {
          const totalSeries = (json.data.totalSeries || []).map((r) => ({ year: String(r.year), outstanding: r.outstanding, monthlyEMI: r.monthlyEMI }));
          const loans = Array.isArray(json.data.loans) ? json.data.loans : [];
          const colors = ['#EF4444', '#F59E0B', '#8B5CF6'];
          const legendLoans = loans.map((l, idx) => ({ id: l.id, name: l.name, color: colors[idx % colors.length] }));
          // Merge per-loan series into rows to match existing chart structure
          const seriesByYear = Object.fromEntries(totalSeries.map((r) => [r.year, { ...r }]));
          loans.forEach((l, idx) => {
            l.series.forEach((p) => {
              const y = String(p.year);
              if (!seriesByYear[y]) seriesByYear[y] = { year: y, outstanding: 0, monthlyEMI: 0 };
              seriesByYear[y][`loan_${l.id}`] = p.balance;
            });
          });
          const mergedRows = Object.values(seriesByYear).sort((a, b) => Number(a.year) - Number(b.year));
          setChartData(mergedRows);
          setTop3Loans(legendLoans);
        } else {
          setChartData([]);
          setTop3Loans([]);
        }
      } catch (e) {
        console.error(e);
        setChartData([]);
        setTop3Loans([]);
      }
    };
    fetchTimeline();
  }, [loans, getDemoQueryParams, getAuthHeaders]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
          <p className="font-medium text-foreground mb-2">Year {label}</p>
          {payload.map((entry, index) => {
            const key = entry.dataKey || entry.name || '';
            let displayName = entry.name;
            let displayValue = entry.value;
            
            if (key === 'outstanding') {
              displayName = 'Total Outstanding';
              displayValue = formatCurrencyINR(entry.value);
            } else if (key === 'monthlyEMI') {
              displayName = 'Monthly EMI';
              displayValue = formatCurrencyINR(entry.value);
            } else if (String(key).startsWith('loan_')) {
              // Find the loan name for this ID
              const loanId = String(key).replace('loan_', '');
              const loan = loans.find(l => l.id === loanId);
              displayName = loan ? loan.name : (entry.name || 'Loan');
              displayValue = formatCurrencyINR(entry.value);
            }
            
            return (
              <p key={index} className="text-sm" style={{ color: entry.color }}>
                {displayName}: {displayValue}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  if (!loans || loans.length === 0) {
    return (
      <div className="h-64 bg-muted/30 rounded-lg flex items-center justify-center">
        <div className="text-center">
          <div className="text-muted-foreground mb-2">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <p className="text-muted-foreground">No debt data available</p>
          <p className="text-sm text-muted-foreground">Add loans to see your debt paydown timeline</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Debt Paydown Timeline</h3>
        <div className="flex items-center gap-4 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-primary rounded-full"></div>
            <span className="text-muted-foreground">Total Outstanding</span>
          </div>
          {top3Loans?.map((l, index) => (
            <div key={l.id} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: l.color }}></div>
              <span className="text-muted-foreground">{l.name}</span>
            </div>
          ))}
        </div>
      </div>
      
      <div className="relative" style={{ height: '256px' }}>
        {(() => {
          const currentYear = new Date().getFullYear();
          const fullTotalYears = chartData.length;
          const truncationConfig = getChartTruncationConfig('DebtPaydown', canProjectBeyond5Years, isDemoModeActive);
          
          // Check if any debt extends beyond year 5
          const hasDebtBeyond5Years = chartData.some((point, idx) => {
            return idx > 5 && (point.outstanding > 0 || Object.keys(point).some(k => k.startsWith('loan_') && point[k] > 0));
          });
          
          // Only apply blur if user is free tier, not in demo mode, AND debt exceeds 5 years
          const shouldTruncate = truncationConfig.shouldTruncate && hasDebtBeyond5Years;
          const freeYearsLimit = 5;
          const displayYearsLimit = freeYearsLimit * 2; // Show 2x free years (10 years total)
          
          // Backend sends ALL years - just show first 10 with blur effect if needed
          const displayData = shouldTruncate 
            ? chartData.slice(0, displayYearsLimit + 1).map((row, idx) => ({
                ...row,
                isBlurred: idx > freeYearsLimit
              }))
            : chartData.map(row => ({ ...row, isBlurred: false }));
          
          // Show year labels every 2 years (only if truncating)
          const xAxisTicks = shouldTruncate 
            ? displayData.filter((d, i) => i % 2 === 0).map(d => d.year)
            : undefined;

          return (
            <>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={displayData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  {shouldTruncate && (
                    <defs>
                      <linearGradient id="blurGradientDebt" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#3B82F6" stopOpacity="1" />
                        <stop offset="48%" stopColor="#3B82F6" stopOpacity="1" />
                        <stop offset="55%" stopColor="#3B82F6" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#3B82F6" stopOpacity="0.15" />
                      </linearGradient>
                    </defs>
                  )}
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="year" 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                    ticks={xAxisTicks}
                    height={shouldTruncate ? 50 : undefined}
                  />
                  <YAxis 
                    stroke="var(--color-muted-foreground)"
                    fontSize={12}
                    tickFormatter={(value) => formatCurrencyINR(value, { compact: true })}
                  />
                  <Tooltip 
                    content={(props) => {
                      if (props.active && props.payload && props.payload.length) {
                        const isBlurred = props.payload[0]?.payload?.isBlurred;
                        if (isBlurred) {
                          return (
                            <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
                              <p className="font-medium text-muted-foreground flex items-center gap-2">
                                <Icon name="Lock" size={14} />
                                Locked Content
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Upgrade to view full projections
                              </p>
                            </div>
                          );
                        }
                        return <CustomTooltip {...props} />;
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="outstanding" 
                    stroke={shouldTruncate ? "url(#blurGradientDebt)" : "#3B82F6"}
                    strokeWidth={3}
                    dot={!shouldTruncate}
                    name="Total Outstanding"
                    strokeDasharray={(entry) => entry?.isBlurred ? "5 5" : "0"}
                  />
                  {top3Loans?.map((l) => (
                    <Line 
                      key={l.id}
                      type="monotone" 
                      dataKey={`loan_${l.id}`}
                      stroke={l.color}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={!shouldTruncate}
                      name={l.name}
                      strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              
              {/* Blurred overlay with gradient and CTA - only if debt exceeds 5 years */}
              {shouldTruncate && fullTotalYears > freeYearsLimit && (
                <div 
                  className="absolute top-0 right-0 pointer-events-none"
                  style={{ 
                    left: '48%',
                    bottom: '50px',
                    background: 'linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.5) 8%, rgba(255, 255, 255, 0.85) 25%, rgba(255, 255, 255, 0.95) 60%)',
                    backdropFilter: 'blur(5px)',
                  }}
                >
                  {/* Dark mode gradient overlay */}
                  <div 
                    className="absolute inset-0 dark:block hidden"
                    style={{ 
                      background: 'linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.4) 8%, rgba(0, 0, 0, 0.75) 25%, rgba(0, 0, 0, 0.9) 60%)',
                    }}
                  />
                  
                  {/* Upgrade CTA Card */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
                    <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-2xl p-4 w-[180px] text-white">
                      <div className="flex items-center justify-center mb-3">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                          <Icon name="Lock" size={22} className="text-white" />
                        </div>
                      </div>
                      <h4 className="font-bold text-center mb-1 text-sm">
                        {(userData?.setup?.projectionHorizon || 20) - freeYearsLimit}+ Years Locked
                      </h4>
                      <p className="text-xs text-center text-white/90 mb-3 leading-tight">
                        Complete debt-free journey visibility.
                      </p>
                      <a
                        href="/upgrade"
                        className="block w-full bg-white text-amber-600 font-semibold text-sm py-2 px-3 rounded-lg text-center hover:bg-amber-50 transition-colors shadow-md"
                      >
                        See My Full Picture
                      </a>
                    </div>
                  </div>
                  
                  {/* Vertical separator line at truncation point */}
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-amber-400 to-transparent opacity-70"
                    style={{ 
                      boxShadow: '0 0 12px rgba(251, 191, 36, 0.7)'
                    }}
                  />
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Current Outstanding</p>
              <p className="text-lg font-semibold text-foreground">
                {formatCurrencyINR(loans.reduce((sum, loan) => sum + loan.outstanding, 0))}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Monthly EMI</p>
              <p className="text-lg font-semibold text-foreground">
                {formatCurrencyINR(loans.reduce((sum, loan) => sum + loan.monthlyEMI, 0))}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active Loans</p>
              <p className="text-lg font-semibold text-foreground">{loans.length}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebtPaydownChart;
